import { bool, elementType, func, number, object, string } from "prop-types";
import { forwardRef, memo, useCallback, useMemo } from "react";

import styled from "styled-components";

import { useActiveAction } from "Dashboard/components/WhatsNext/whatsNextStore";
import {
  navSectionKeys,
  useActiveSectionKey,
  useHasActiveSection,
} from "NavBar/navBarStore";
import { useIsFullScreen } from "NavBar/stateHooks";
import { nonOpaqueTextWhite } from "NavBar/styleConstants";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrNumericIndicator from "UI/FnrNumericIndicator";
import FnrTypography from "UI/FnrTypography";

import { addOpacity } from "Utilities/Colors/colorUtils";
import useHasFinePointer from "Utilities/Hooks/useHasFinePointer";
import {
  useIsFocusVisible,
  useIsHovered,
} from "Utilities/Hooks/useIsHoverFocused";

import { navSectionPadding } from "../NavSection";

import Squiggle from "./Squiggle";

const Header = memo(
  forwardRef(
    (
      {
        navSectionKey,
        isActive,
        color,
        icon,
        label,
        count,
        isSquiggleFlashing,
        headerTextRef,
        onHeaderMouseEnter,
        defaultRoute,
        HeaderSuffixComponent,
      },
      ref
    ) => {
      const isHovered = useIsHovered(ref);
      const isFocusVisible = useIsFocusVisible(ref);
      const hasFinePointer = useHasFinePointer();

      const hasActiveSection = useHasActiveSection();
      const [activeSectionKey, setActiveSectionKey] = useActiveSectionKey();
      const [activeWhatsNextAction] = useActiveAction();
      const isFullScreen = useIsFullScreen();

      const handleHeaderClick = useCallback(() => {
        if (isActive) {
          setActiveSectionKey(navSectionKeys.nothing);
          if (!isFullScreen) window.fnReactBus.goToUrl("/action-center");
        } else {
          setActiveSectionKey(navSectionKey);

          // Skip default navigation when WhatsNext arrow flow is active
          if (activeWhatsNextAction) return;

          // Skip default navigation in full-screen mode (nav is an overlay)
          if (isFullScreen) return;

          // Navigate to default route for the section
          if (defaultRoute) {
            window.fnReactBus.goToUrl(defaultRoute);
          }
        }
      }, [
        isActive,
        navSectionKey,
        setActiveSectionKey,
        activeWhatsNextAction,
        isFullScreen,
        defaultRoute,
      ]);

      const headerIsLarge = useMemo(
        () => isActive || activeSectionKey === navSectionKeys.nothing,
        [isActive, activeSectionKey]
      );

      const iconSize = useMemo(
        () => (headerIsLarge ? "xl" : "md"),
        [headerIsLarge]
      );
      const fontSize = useMemo(
        () => (headerIsLarge ? "1.25rem" : ".95rem"),
        [headerIsLarge]
      );

      const mountSquiggle = useMemo(
        () => hasFinePointer || isSquiggleFlashing,
        [hasFinePointer, isSquiggleFlashing]
      );
      const showSquiggle = useMemo(
        () =>
          Boolean(
            isActive || isHovered || isFocusVisible || isSquiggleFlashing
          ),
        [isActive, isHovered, isFocusVisible, isSquiggleFlashing]
      );

      const squiggleColor = useMemo(
        () => (isActive ? addOpacity(nonOpaqueTextWhite, 0) : color),
        [color, isActive]
      );

      const smallerSize = useMemo(
        () => !isActive && hasActiveSection,
        [hasActiveSection, isActive]
      );

      return (
        <SFnrButtonWrapper
          onClick={handleHeaderClick}
          onMouseEnter={onHeaderMouseEnter}
          ref={ref}
        >
          <HeaderWrapper $increaseSpacing={smallerSize}>
            <FnrContainedIcon
              icon={icon}
              size={iconSize}
              color="textLight"
              noAnimation
              shadow
            />
            <LabelAndIndicatorWrapper>
              <AbsoluteWrapper>
                <LabelSquiggleWrapper>
                  <FnrTypography
                    fontSize={fontSize}
                    color="textLight"
                    type="copy"
                    bold={false}
                    noWrap
                    shadow
                    ref={headerTextRef}
                  >
                    {label}
                  </FnrTypography>
                  {mountSquiggle && (
                    <Squiggle
                      show={showSquiggle}
                      color={squiggleColor}
                      scaleDown={smallerSize}
                      isFlashing={isSquiggleFlashing}
                    />
                  )}
                </LabelSquiggleWrapper>
                <FnrNumericIndicator count={count} show={!isActive} inline />
                {HeaderSuffixComponent && isActive && <HeaderSuffixComponent />}
              </AbsoluteWrapper>
            </LabelAndIndicatorWrapper>
          </HeaderWrapper>
        </SFnrButtonWrapper>
      );
    }
  )
);

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  &:focus-visible {
    background-color: transparent;
  }
`;

const LabelAndIndicatorWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const inactiveOffset = 6.5; // Gets the gap between icon and lable to line up with active item.
// Safari doesn't collapse the icon's negative margins in line boxes, making
// the header taller than on Chrome. Explicit height normalizes cross-browser.
const inactiveIconLayoutHeight = 16; // md icon: 28px visual - 2*6px negative margin = 16px layout
const HeaderWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ $increaseSpacing }) =>
    !$increaseSpacing
      ? navSectionPadding
      : navSectionPadding + inactiveOffset}px;
  margin-left: ${({ $increaseSpacing }) =>
    !$increaseSpacing ? 0 : inactiveOffset}px;
  transition: margin 300ms, gap 300ms;
  ${({ $increaseSpacing }) =>
    $increaseSpacing && `height: ${inactiveIconLayoutHeight}px;`}
`;

const LabelSquiggleWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  max-width: min-content;
`;

const AbsoluteWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
`;

Header.propTypes = {
  navSectionKey: string,
  isActive: bool,
  color: string,
  icon: string,
  label: string,
  count: number,
  isSquiggleFlashing: bool,
  headerTextRef: object,
  onHeaderMouseEnter: func,
  defaultRoute: string,
  HeaderSuffixComponent: elementType,
};

export default Header;
